import type { DataOption } from '@/common.types'
import type { FilterRuleEntity } from '@/shared/components/query-builder'
import type { StrategyModalFormData } from '../model/types'

import { PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { getLocalization } from '@/shared/components/filter/localization'
import {
	QueryBuilder,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import Combobox from '@/shared/components/query-builder/components/Combobox'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useAppStore } from '@/shared/contexts/app'

import { useStrategyFieldsConfig } from '../lib/useStrategyFieldsConfig'

export const AnchorSelectionTab = () => {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control, formState } = useFormContext<StrategyModalFormData>()

	const { fields, append, remove, update } = useFieldArray({
		control,
		name: 'settings.anchor_selection.sorting',
	})

	const {
		isLoading,
		fieldConfig,
		sortableColumnOptions: columnOptions,
	} = useStrategyFieldsConfig()

	const directionOptions: DataOption[] = [
		{ value: 'asc', label: t('app.ascending', { defaultValue: 'Ascending' }) },
		{
			value: 'desc',
			label: t('app.descending', { defaultValue: 'Descending' }),
		},
	]

	const handleAddColumn = () => {
		const usedNames = fields.map((f) => f.name)
		const nextDefault =
			(columnOptions.find((o) => !usedNames.includes(o.value as string))
				?.value as string) || ''
		append({ name: nextDefault, direction: 'asc' as const })
	}

	return isLoading ? (
		<div className='flex flex-col space-y-2'>
			<Skeleton className='h-10 w-full' />
			<Skeleton className='h-10 w-full' />
		</div>
	) : (
		<ErrorBoundary
			fallback={() => (
				<Alert variant='destructive'>
					<AlertDescription>
						🐞{' '}
						{t('ui_fragment_error', {
							defaultValue:
								'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
						})}
					</AlertDescription>
				</Alert>
			)}
		>
			<div className='space-y-6'>
				<Separator className='mb-4' />

				{/* Filters */}
				<div className='grid gap-1.5'>
					<Label className='text-sm'>{t('app.filters')}</Label>
					<Controller
						name='settings.anchor_selection.filters'
						control={control}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<QueryBuilder
								dateFormat={dateFormat}
								rules={value || []}
								fieldConfig={fieldConfig}
								locale={getLocalization(t)}
								onRulesChange={(rules: FilterRuleEntity[]) => {
									onChange(rules.map(formatFilterRule))
								}}
							>
								<QueryBuilderEmpty className='border-b' />
								<QueryBuilderRulesList className='max-h-75 overflow-y-auto border-y py-2' />
								{error && (
									<p className='text-sm text-destructive'>
										{error.message || t('validation.invalid_rules')}
									</p>
								)}
								<QueryBuilderAddButton
									data-testid='anchor-selection-filters-add'
									className='mr-auto'
								>
									<PlusIcon />
									{t('filters_add_filter')}
								</QueryBuilderAddButton>
							</QueryBuilder>
						)}
					/>
				</div>

				{/* Sort columns */}
				<div className='space-y-2'>
					<Label className='text-sm'>
						{t(
							'anchoring_management.strategy.tab.anchor_selection.columns.title',
							{ defaultValue: 'Sort columns' }
						)}
					</Label>
					<div className='space-y-2'>
						{fields.map((field, index) => (
							<div key={field.id} className='flex items-center gap-2'>
								<Combobox
									name={`sort-column-${index}`}
									value={field.name}
									onChange={(name) =>
										update(index, { name, direction: field.direction })
									}
									options={columnOptions
										.filter(
											(o) =>
												!fields.some((f) => f.name === o.value) ||
												o.value === field.name
										)
										.map((o) => ({
											value: o.value as string,
											label: o.label,
										}))}
									placeholder={t('general_select')}
									locale={getLocalization(t)}
									className='w-1/2'
									data-testid={`anchor-selection-column-${index}`}
								/>
								<Select
									value={field.direction}
									onValueChange={(direction: 'asc' | 'desc') =>
										update(index, { name: field.name, direction })
									}
								>
									<SelectTrigger
										className='w-36'
										data-testid={`anchor-selection-direction-${index}`}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{directionOptions.map((opt) => (
												<SelectItem key={opt.value} value={opt.value as string}>
													{opt.label}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
								<Button
									size='icon'
									variant='ghost'
									onClick={() => remove(index)}
									disabled={fields.length <= 1}
								>
									<XIcon />
								</Button>
							</div>
						))}
					</div>
					<Button
						variant='ghost'
						className='mr-auto'
						data-testid='anchor-selection-add-column-button'
						disabled={fields.length === columnOptions.length}
						onClick={handleAddColumn}
					>
						<PlusIcon />
						{t(
							'anchoring_management.strategy.tab.anchor_selection.add_column',
							{ defaultValue: 'Add column' }
						)}
					</Button>
					{formState.errors?.settings?.anchor_selection?.sorting?.root
						?.message && (
						<p className='text-sm text-destructive'>
							{formState.errors.settings.anchor_selection.sorting.root.message}
						</p>
					)}
				</div>
			</div>
		</ErrorBoundary>
	)
}
